import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowRight, Clock, Tag } from 'lucide-react';
import { Link } from 'react-router-dom';
import { 
  getPublishedPosts, 
  getFeaturedPosts, 
  getPostsByCategory, 
  blogCategories,
  searchPosts,
  BlogPost as BlogPostType 
} from '../data/blogPosts';

const Blog: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Get dynamic data
  const allPosts = getPublishedPosts();
  const featuredPosts = getFeaturedPosts();
  const featuredPost = featuredPosts[0]; // Get the first featured post
  
  // Filter posts based on selected category and search
  const getFilteredPosts = (): BlogPostType[] => {
    let posts = allPosts;
    
    // Filter by category
    if (selectedCategory !== 'All') {
      posts = posts.filter(post => post.category.name === selectedCategory);
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      posts = searchPosts(searchQuery);
      if (selectedCategory !== 'All') {
        posts = posts.filter(post => post.category.name === selectedCategory);
      }
    }
    
    // Exclude featured post from regular listing
    return posts.filter(post => !post.featured);
  };
  
  const filteredPosts = getFilteredPosts();
  const categories = ['All', ...blogCategories.map(cat => cat.name)];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-slate-100/50 dark:bg-slate-900/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-slate-900 dark:text-white">
              Healthcare <span className="gradient-text">Insights</span>
            </h1>
            <p className="text-xl text-slate-700 dark:text-gray-300 max-w-3xl mx-auto">
              Stay informed with the latest trends, best practices, and innovations in healthcare technology.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Post */}
      <section className="py-20 bg-white dark:bg-gray-900/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold mb-8 text-slate-900 dark:text-white">Featured Article</h2>
            <div className="glass-effect glass-effect-hover rounded-2xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="p-8">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="bg-primary-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {featuredPost.category.name}
                    </span>
                    <span className="text-slate-600 dark:text-gray-400 text-sm">Featured</span>
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-slate-900 dark:text-white">{featuredPost.title}</h3>
                  <p className="text-slate-700 dark:text-gray-300 mb-6">{featuredPost.excerpt}</p>
                  <div className="flex items-center gap-6 text-sm text-slate-600 dark:text-gray-400 mb-6">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      {featuredPost.author.name}
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {new Date(featuredPost.publishedDate).toLocaleDateString()}
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      {featuredPost.readTime}
                    </div>
                  </div>
                  <Link to={`/blog/${featuredPost.slug}`}>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      className="btn-primary flex items-center gap-2"
                    >
                      Read Article
                      <ArrowRight className="w-5 h-5" />
                    </motion.button>
                  </Link>
                </div>
                <div className="h-64 lg:h-auto">
                  <div 
                    className="w-full h-full bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg"
                    style={{ 
                      backgroundImage: `url(${featuredPost.image})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Category Filter */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-12"
          >
            <div className="flex flex-wrap gap-3 justify-center">
              {categories.map((category, index) => (
                <motion.button
                  key={category}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full transition-all duration-300 ${
                    selectedCategory === category
                      ? 'bg-primary-500 text-white' 
                      : 'glass-effect glass-effect-hover text-slate-700 dark:text-gray-300'
                  }`}
                >
                  <Tag className="w-4 h-4 inline mr-2" />
                  {category}
                </motion.button>
              ))}
            </div>
          </motion.div>

          {/* Blog Posts Grid */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h2 className="text-3xl font-bold mb-8 text-slate-900 dark:text-white">Latest Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => (
                <Link key={post.id} to={`/blog/${post.slug}`}>
                  <motion.article
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="glass-effect glass-effect-hover rounded-xl overflow-hidden group cursor-pointer h-full flex flex-col"
                  >
                  <div 
                    className="h-48 bg-gradient-to-br from-primary-500 to-secondary-500"
                    style={{ 
                      backgroundImage: `url(${post.image})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  />
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="bg-primary-500/20 text-primary-400 px-2 py-1 rounded text-xs font-semibold">
                        {post.category.name}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold mb-3 group-hover:text-primary-400 transition-colors text-slate-900 dark:text-white">
                      {post.title}
                    </h3>
                    <p className="text-slate-700 dark:text-gray-300 text-sm mb-4 line-clamp-3 flex-1">
                      {post.excerpt}
                    </p>
                    <div className="grid grid-cols-3 gap-2 text-xs text-slate-600 dark:text-gray-400 mt-auto">
                      <div className="flex items-center gap-1 justify-center">
                        <User className="w-3 h-3" />
                        <span className="truncate">{post.author.name}</span>
                      </div>
                      <div className="flex items-center gap-1 justify-center">
                        <Calendar className="w-3 h-3" />
                        <span className="truncate">{new Date(post.publishedDate).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center gap-1 justify-center">
                        <Clock className="w-3 h-3" />
                        <span className="truncate">{post.readTime}</span>
                      </div>
                    </div>
                  </div>
                  </motion.article>
                </Link>
              ))}
            </div>
          </motion.div>

          {/* TODO: Newsletter Signup */}
          {/* <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="glass-effect p-8 rounded-2xl mt-16 text-center"
          >
            <h3 className="text-2xl font-bold mb-4 text-slate-900 dark:text-white">Stay Updated</h3>
            <p className="text-slate-700 dark:text-gray-300 mb-6 max-w-2xl mx-auto">
              Subscribe to our newsletter and get the latest healthcare technology insights delivered to your inbox.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:border-primary-500 focus:outline-none transition-colors"
              />
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="btn-primary whitespace-nowrap"
              >
                Subscribe
              </motion.button>
            </div>
          </motion.div> */}
        </div>
      </section>
    </div>
  );
};

export default Blog; 